/**
 * School money — saving it. Every change to money goes through here, and each is all-or-nothing (a
 * Firestore transaction): the payment, its numbered receipt, the invoice's new balance and the finance
 * log entry are saved together or not at all. Payments are never deleted: a mistake is reversed, with a
 * reason, and both stay on record.
 */
import { collection, doc, getDocs, limit, query, runTransaction, serverTimestamp, where, writeBatch, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import type { FeeCategory, FeeTemplate, Invoice, Student } from '../../types';
import { alreadyBilled, balanceOf, invoiceItemsFor, receiptNumber, registrationNumber, storedStatus, termNumber, type PaymentMethod } from './money';

export interface Actor { id: string; name: string }
export interface SchoolRef { id: string; prefix: string }

/** a payment as saved (older payments have only the first fields) */
export interface PaymentRecord {
    id: string; schoolId: string; invoiceId: string; studentId: string; amount: number; method: PaymentMethod | string; paidAt: number; paymentRef: string;
    receiptNo?: string; receivedBy?: string; receivedByName?: string; createdAt?: number; source?: 'office' | 'transfer' | 'online';
    status?: 'valid' | 'reversed'; reversedAt?: number; reversedBy?: string; reversedByName?: string; reversalReason?: string; claimId?: string; note?: string;
}

export type FinanceAction = 'payment' | 'reversal' | 'invoices' | 'invoice-edit' | 'invoice-void' | 'discount' | 'transfer-approved' | 'transfer-rejected' | 'payroll' | 'expense' | 'income' | 'record' | 'enrolment';
export interface FinanceLogEntry { schoolId: string; at: number; by: string; byName: string; action: FinanceAction; amount?: number; ref?: string; studentId?: string; detail: string }

const logRef = () => doc(collection(db, 'finance_log'));
/** add to the finance log (outside a transaction) */
export async function financeLog(e: Omit<FinanceLogEntry, 'at'>) {
    try { await addDoc(collection(db, 'finance_log'), { ...e, at: Date.now() }); } catch (err) { console.warn('[finance] log', err); }
}

// ─── payments ────────────────────────────────────────────────────────────────

export interface ReceiveInput {
    school: SchoolRef; by: Actor; invoiceId: string; amount: number; method: PaymentMethod | string;
    /** the bank, POS or teller reference (blank for cash: the receipt number is used) */
    reference?: string; paidAt?: number; source?: PaymentRecord['source']; claimId?: string; note?: string;
}

/** a reference already used on a payment that was not reversed */
export async function referenceUsed(schoolId: string, reference: string): Promise<PaymentRecord | null> {
    const ref = reference.trim();
    if (!ref) return null;
    const snap = await getDocs(query(collection(db, 'payments'), where('schoolId', '==', schoolId), where('paymentRef', '==', ref), limit(5)));
    const hit = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }) as PaymentRecord).find(p => p.status !== 'reversed');
    return hit || null;
}

/**
 * Receive a payment against one invoice. Refuses a zero or negative amount, more than the balance,
 * or a reference already used. Gives the next receipt number for the school and year.
 */
export async function receivePayment(i: ReceiveInput): Promise<PaymentRecord> {
    const amount = Math.round(Number(i.amount) * 100) / 100;
    if (!(amount > 0)) throw new Error('Enter an amount greater than zero.');
    const reference = (i.reference || '').trim();
    if (reference) {
        const used = await referenceUsed(i.school.id, reference);
        if (used) throw new Error(`This reference was already used on receipt ${used.receiptNo || used.paymentRef} (${new Date(used.paidAt).toLocaleDateString('en-GB')}). Check it is not the same payment.`);
    }
    const paidAt = i.paidAt || Date.now();
    const year = new Date(paidAt).getFullYear();
    const invRef = doc(db, 'invoices', i.invoiceId);
    const counterRef = doc(db, 'finance_counters', i.school.id);
    const payRef = doc(collection(db, 'payments'));
    return runTransaction(db, async tx => {
        const invSnap = await tx.get(invRef);
        if (!invSnap.exists()) throw new Error('The invoice was not found.');
        const inv = { id: invSnap.id, ...(invSnap.data() as any) } as Invoice & { void?: boolean; schoolId?: string };
        if (inv.void) throw new Error('This invoice was cancelled.');
        const balance = balanceOf(inv);
        if (amount > balance + 0.005) throw new Error(`The balance on this invoice is ₦${balance.toLocaleString('en-NG')}. Enter that amount or less.`);
        const counter = (await tx.get(counterRef)).data() || {};
        const n = ((counter.receipts || {})[year] || 0) + 1;
        const receiptNo = receiptNumber(i.school.prefix, year, n);
        const payment: PaymentRecord = {
            id: payRef.id, schoolId: i.school.id, invoiceId: inv.id, studentId: inv.studentId, amount, method: i.method,
            paidAt, paymentRef: reference || receiptNo, receiptNo, receivedBy: i.by.id, receivedByName: i.by.name, createdAt: Date.now(),
            source: i.source || 'office', status: 'valid', ...(i.claimId ? { claimId: i.claimId } : {}), ...(i.note ? { note: i.note } : {}),
        };
        const paid = Math.round(((Number(inv.amountPaid) || 0) + amount) * 100) / 100;
        tx.set(counterRef, { schoolId: i.school.id, receipts: { ...(counter.receipts || {}), [year]: n } }, { merge: true });
        tx.set(payRef, payment);
        tx.update(invRef, { amountPaid: paid, status: storedStatus(Number(inv.total) || 0, paid), lastPaymentAt: paidAt });
        tx.set(logRef(), { schoolId: i.school.id, at: Date.now(), by: i.by.id, byName: i.by.name, action: 'payment', amount, ref: receiptNo, studentId: inv.studentId, detail: `${i.method}${reference ? ` · ref ${reference}` : ''} · balance now ₦${Math.max(0, (Number(inv.total) || 0) - paid).toLocaleString('en-NG')}` } as FinanceLogEntry);
        return payment;
    });
}

/** reverse a payment entered by mistake: the payment stays on record, marked reversed, and the invoice balance goes back up */
export async function reversePayment(paymentId: string, reason: string, by: Actor): Promise<void> {
    if (reason.trim().length < 5) throw new Error('Give the reason for the reversal.');
    const payRef = doc(db, 'payments', paymentId);
    await runTransaction(db, async tx => {
        const ps = await tx.get(payRef);
        if (!ps.exists()) throw new Error('The payment was not found.');
        const p = { id: ps.id, ...(ps.data() as any) } as PaymentRecord;
        if (p.status === 'reversed') throw new Error('This payment was already reversed.');
        const invRef = doc(db, 'invoices', p.invoiceId);
        const is = await tx.get(invRef);
        tx.update(payRef, { status: 'reversed', reversedAt: Date.now(), reversedBy: by.id, reversedByName: by.name, reversalReason: reason.trim() });
        if (is.exists()) {
            const inv = is.data() as Invoice;
            const paid = Math.max(0, Math.round(((Number(inv.amountPaid) || 0) - p.amount) * 100) / 100);
            tx.update(invRef, { amountPaid: paid, status: storedStatus(Number(inv.total) || 0, paid) });
        }
        tx.set(logRef(), { schoolId: p.schoolId, at: Date.now(), by: by.id, byName: by.name, action: 'reversal', amount: p.amount, ref: p.receiptNo || p.paymentRef, studentId: p.studentId, detail: `Reversed: ${reason.trim()}` } as FinanceLogEntry);
    });
}

// ─── invoices ────────────────────────────────────────────────────────────────

export interface GenerateInput {
    school: SchoolRef; by: Actor; students: Student[]; existing: (Invoice & { void?: boolean })[];
    term: number | string; session: string; dueDate: number; templates: FeeTemplate[]; categories: FeeCategory[];
}
export interface GenerateResult { created: number; skipped: { studentId: string; reason: string }[]; total: number }

/** bill students for a term: one invoice each, skipping anyone already billed for that term and session */
export async function generateInvoices(g: GenerateInput): Promise<GenerateResult> {
    if (!g.templates.length) throw new Error('Choose at least one fee template.');
    const term = termNumber(g.term);
    if (!term) throw new Error('Choose the term.');
    const templateIds = g.templates.map(t => t.id);
    const skipped: GenerateResult['skipped'] = [];
    const rows: any[] = [];
    for (const s of g.students) {
        if (alreadyBilled(g.existing, s.id, term, g.session, templateIds)) { skipped.push({ studentId: s.id, reason: 'already billed for this term' }); continue; }
        const items = invoiceItemsFor(s, g.templates, g.categories);
        const total = items.reduce((a, it) => a + it.amount, 0);
        rows.push({ studentId: s.id, schoolId: g.school.id, term, session: g.session, items, total, amountPaid: 0, status: storedStatus(total, 0), createdAt: Date.now(), dueDate: g.dueDate, templateIds, createdBy: g.by.id });
    }
    // Firestore takes at most 500 writes in one batch
    for (let k = 0; k < rows.length; k += 450) {
        const b = writeBatch(db);
        for (const r of rows.slice(k, k + 450)) b.set(doc(collection(db, 'invoices')), r);
        await b.commit();
    }
    const total = rows.reduce((a, r) => a + r.total, 0);
    if (rows.length) await financeLog({ schoolId: g.school.id, by: g.by.id, byName: g.by.name, action: 'invoices', amount: total, detail: `${rows.length} invoice(s) for term ${term}, ${g.session}${skipped.length ? `; ${skipped.length} skipped (already billed)` : ''}` });
    return { created: rows.length, skipped, total };
}

/** cancel an invoice raised by mistake (only when nothing has been paid on it); it stays on record as void */
export async function voidInvoice(inv: Invoice, reason: string, by: Actor, schoolId: string) {
    if ((Number(inv.amountPaid) || 0) > 0) throw new Error('Money has been paid on this invoice. Reverse the payments first.');
    if (reason.trim().length < 5) throw new Error('Give the reason for cancelling the invoice.');
    const b = writeBatch(db);
    b.update(doc(db, 'invoices', inv.id), { void: true, voidReason: reason.trim(), voidedAt: Date.now(), voidedBy: by.id });
    b.set(logRef(), { schoolId, at: Date.now(), by: by.id, byName: by.name, action: 'invoice-void', amount: inv.total, studentId: inv.studentId, ref: inv.id.slice(0, 8), detail: `Cancelled: ${reason.trim()}` });
    await b.commit();
}

/** apply a discount, worked out from the invoice's own lines, and update its status */
export async function applyDiscount(inv: Invoice, discount: { type: 'percentage' | 'fixed'; value: number; name?: string }, by: Actor, schoolId: string, total: number) {
    const paid = Number(inv.amountPaid) || 0;
    if (total < paid) throw new Error('The discount would make the fees less than what has already been paid.');
    const b = writeBatch(db);
    b.update(doc(db, 'invoices', inv.id), { total, discount: { type: discount.type, value: discount.value, ...(discount.name ? { name: discount.name } : {}) }, status: storedStatus(total, paid) });
    b.set(logRef(), { schoolId, at: Date.now(), by: by.id, byName: by.name, action: 'discount', amount: (Number(inv.total) || 0) - total, studentId: inv.studentId, ref: inv.id.slice(0, 8), detail: `${discount.name || 'Discount'}: ${discount.type === 'percentage' ? `${discount.value}%` : `₦${discount.value.toLocaleString('en-NG')}`}` });
    await b.commit();
}

// ─── bank transfers sent in by parents ───────────────────────────────────────

export interface PaymentClaim {
    id: string; schoolId: string; studentId: string; studentName?: string; invoiceId: string; amount: number; reference: string; bank?: string;
    paidOn: number; proofPath?: string; proofUrl?: string; note?: string; status: 'pending' | 'approved' | 'rejected'; createdAt: number;
    decidedAt?: number; decidedBy?: string; decidedByName?: string; rejectReason?: string; paymentId?: string; receiptNo?: string;
}

/** a parent's bank transfer, approved: it becomes a payment with a receipt */
export async function approveClaim(c: PaymentClaim, school: SchoolRef, by: Actor, amount = c.amount): Promise<PaymentRecord> {
    const p = await receivePayment({ school, by, invoiceId: c.invoiceId, amount, method: 'Bank Transfer', reference: c.reference, paidAt: c.paidOn, source: 'transfer', claimId: c.id });
    const b = writeBatch(db);
    b.update(doc(db, 'payment_claims', c.id), { status: 'approved', decidedAt: Date.now(), decidedBy: by.id, decidedByName: by.name, paymentId: p.id, receiptNo: p.receiptNo, ...(amount !== c.amount ? { approvedAmount: amount } : {}) });
    b.set(doc(collection(db, 'notifications')), { title: 'Payment confirmed', message: `Your payment of ₦${amount.toLocaleString('en-NG')} was received. Receipt ${p.receiptNo}.`, type: 'finance', recipientId: c.studentId, schoolId: school.id, createdAt: Date.now(), read: false, readBy: [] });
    await b.commit();
    return p;
}
export async function rejectClaim(c: PaymentClaim, reason: string, school: SchoolRef, by: Actor) {
    if (reason.trim().length < 5) throw new Error('Tell the parent why, so they can correct it.');
    const b = writeBatch(db);
    b.update(doc(db, 'payment_claims', c.id), { status: 'rejected', decidedAt: Date.now(), decidedBy: by.id, decidedByName: by.name, rejectReason: reason.trim() });
    b.set(doc(collection(db, 'notifications')), { title: 'Payment not confirmed', message: `Your transfer of ₦${c.amount.toLocaleString('en-NG')} (ref ${c.reference}) could not be confirmed: ${reason.trim()}`, type: 'finance', recipientId: c.studentId, schoolId: school.id, createdAt: Date.now(), read: false, readBy: [] });
    b.set(logRef(), { schoolId: school.id, at: Date.now(), by: by.id, byName: by.name, action: 'transfer-rejected', amount: c.amount, studentId: c.studentId, ref: c.reference, detail: reason.trim() });
    await b.commit();
}

// ─── registration numbers ────────────────────────────────────────────────────

/** the next registration number for the school and year (never repeats) */
export async function nextRegistrationNumber(school: SchoolRef, year = new Date().getFullYear()): Promise<string> {
    const counterRef = doc(db, 'finance_counters', school.id);
    return runTransaction(db, async tx => {
        const c = (await tx.get(counterRef)).data() || {};
        const n = ((c.registrations || {})[year] || 0) + 1;
        tx.set(counterRef, { schoolId: school.id, registrations: { ...(c.registrations || {}), [year]: n } }, { merge: true });
        return registrationNumber(school.prefix, year, n);
    });
}

export { serverTimestamp };
